import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type RequestStatus,
  useAddApprovedPartner,
  useApprovedPartners,
  useDecideRequest,
  usePartnershipRequests,
  useRemoveApprovedPartner,
  useSubmitRequest,
} from '@/lib/api/partnerships';
import { config } from '@/lib/config';
import { useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
/**
 * Tenant Settings modal — cross-tenant partnership management.
 *
 * Reworked from the mockup's instant "Enable / approve-by-name" design to the
 * shipped Partnership Controller's async request → approve/deny model:
 *   • Partnerships     — submit a partnership request + see all requests with
 *                        status, and approve/deny pending ones inline.
 *   • Access Approvals — the approved-partners allowlist (add / remove).
 *
 * Backed by the live API in `live` mode; by MSW in-memory state in `mock` mode.
 */
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';

type Tab = 'partnerships' | 'approvals';

export function TenantSettingsModal() {
  const open = useUiStore((s) => s.tenantSettingsOpen);
  const setOpen = useUiStore((s) => s.setTenantSettingsOpen);
  const [tab, setTab] = useState<Tab>('partnerships');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Tenant Settings</DialogTitle>
          <DialogDescription>
            {config.tenant.label} · {config.tenant.id}.flysafe.live — cross-tenant partnerships
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-(--color-border) -mt-1">
          <TabBtn on={tab === 'partnerships'} onClick={() => setTab('partnerships')}>
            Partnerships
          </TabBtn>
          <TabBtn on={tab === 'approvals'} onClick={() => setTab('approvals')}>
            Access Approvals
          </TabBtn>
        </div>

        <div className="min-h-[260px] max-h-[60dvh] overflow-y-auto">
          {tab === 'partnerships' ? <PartnershipsPanel /> : <ApprovalsPanel />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({
  on,
  onClick,
  children,
}: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-[0.846em] font-semibold border-b-2 -mb-px transition-colors',
        on
          ? 'border-(--color-green) text-(--color-green)'
          : 'border-transparent text-(--color-text3) hover:text-(--color-text)',
      )}
    >
      {children}
    </button>
  );
}

const statusTone: Record<RequestStatus, 'warn' | 'ok' | 'crit'> = {
  pending: 'warn',
  approved: 'ok',
  denied: 'crit',
};

function PartnershipsPanel() {
  const { data: requests, isLoading, isError } = usePartnershipRequests();
  const submit = useSubmitRequest();
  const approve = useDecideRequest('approve');
  const deny = useDecideRequest('deny');
  const [name, setName] = useState('');

  const onSubmit = async () => {
    const t = name.trim();
    if (!t) return;
    try {
      await submit.mutateAsync(t);
      setName('');
      toast.success(`Partnership request sent to ${t}`);
    } catch {
      toast.error('Could not submit request');
    }
  };

  return (
    <div className="space-y-3 pt-3">
      <div>
        <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
          Request a partnership
        </div>
        <div className="flex gap-2 mt-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
            placeholder="Partner tenant ID (e.g. RegionAir)"
            className="flex-1 px-2.5 py-1.5 text-[0.846em] rounded-md border border-(--color-border) bg-(--color-bg) focus:outline-none focus:border-(--color-green)"
          />
          <Button size="sm" onClick={onSubmit} disabled={submit.isPending || !name.trim()}>
            {submit.isPending ? 'Sending…' : 'Request'}
          </Button>
        </div>
        <p className="text-[0.615em] text-(--color-text3) mt-1">
          Subscription topic on approval:{' '}
          <span className="font-mono">flysafe/v1/partner/+/telemetry</span>
        </p>
      </div>

      <div>
        <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mb-1">
          Partnership Requests
        </div>
        {isLoading && <Muted>Loading requests…</Muted>}
        {isError && <Muted>Could not load requests.</Muted>}
        {requests && requests.length === 0 && <Muted>No partnership requests.</Muted>}
        <div className="space-y-1.5">
          {requests?.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between gap-2 rounded-md border border-(--color-border) px-2.5 py-2"
            >
              <div className="min-w-0">
                <div className="text-[0.846em] font-semibold truncate">{r.requesting_tenant}</div>
                <div className="text-[0.615em] font-mono text-(--color-text3)">
                  #{r.id}
                  {r.notes ? ` · ${r.notes}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge variant={statusTone[r.status]}>{r.status}</Badge>
                {r.status === 'pending' && (
                  <>
                    <Button
                      size="xs"
                      onClick={() => approve.mutate({ id: r.id })}
                      disabled={approve.isPending}
                    >
                      Approve
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => deny.mutate({ id: r.id })}
                      disabled={deny.isPending}
                    >
                      Deny
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ApprovalsPanel() {
  const { data: approved, isLoading, isError } = useApprovedPartners();
  const add = useAddApprovedPartner();
  const remove = useRemoveApprovedPartner();
  const [name, setName] = useState('');

  const onAdd = async () => {
    const t = name.trim();
    if (!t) return;
    try {
      await add.mutateAsync(t);
      setName('');
      toast.success(`${t} approved`);
    } catch {
      toast.error('Could not approve partner');
    }
  };

  return (
    <div className="space-y-3 pt-3">
      <div>
        <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
          Approve a partner tenant
        </div>
        <div className="flex gap-2 mt-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onAdd()}
            placeholder="Tenant ID to grant access (e.g. NorthernSkies)"
            className="flex-1 px-2.5 py-1.5 text-[0.846em] rounded-md border border-(--color-border) bg-(--color-bg) focus:outline-none focus:border-(--color-green)"
          />
          <Button size="sm" onClick={onAdd} disabled={add.isPending || !name.trim()}>
            {add.isPending ? 'Adding…' : 'Approve'}
          </Button>
        </div>
        <p className="text-[0.615em] text-(--color-text3) mt-1">
          Approved tenants may consume this tenant's telemetry feed.
        </p>
      </div>

      <div>
        <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mb-1">
          Approved Partners
        </div>
        {isLoading && <Muted>Loading…</Muted>}
        {isError && <Muted>Could not load approved partners.</Muted>}
        {approved && approved.length === 0 && <Muted>No tenants approved.</Muted>}
        <div className="space-y-1.5">
          {approved?.map((p) => (
            <div
              key={p.partner_tenant}
              className="flex items-center justify-between gap-2 rounded-md border border-(--color-border) px-2.5 py-2"
            >
              <div className="min-w-0">
                <div className="text-[0.846em] font-semibold truncate">{p.partner_tenant}</div>
                <div className="text-[0.615em] font-mono text-(--color-text3)">
                  added {new Date(p.added_at).toLocaleDateString()}
                </div>
              </div>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => remove.mutate(p.partner_tenant)}
                disabled={remove.isPending}
              >
                Revoke
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <div className="text-[0.769em] text-(--color-text3) py-3">{children}</div>;
}
